import axios from 'axios';
import { z } from 'zod';
import type { OrderShelfLocationMap } from '../types/apiTypes';
import { normalizeSku } from '../utils/normalizeSku';
import { dedupeInFlight, skuListCacheKey } from '../utils/inFlightByKey';
import { validateResponse } from './validateResponse';

const PrefixMapSchema = z.record(z.string(), z.string());
const OrderShelfLocationMapSchema = z.record(
  z.string(),
  z.object({ shelfIndex: z.string().nullable(), type: z.string().nullable() }),
);

/**
 * Fetch storage_id_prefix → SKU map for QR scan mode (pick bin, checked_in only).
 * Single batch call replaces per-SKU lookups.
 */
export const fetchPrefixMap = async (skus: string[]): Promise<Record<string, string>> => {
  const list = Array.isArray(skus) ? skus : [];
  const key = `prefix-map:${skuListCacheKey(list)}`;
  return dedupeInFlight(key, async () => {
    try {
      const response = await axios.post('/api/sku-shelf-index/prefix-map', { skus: list });
      return validateResponse(PrefixMapSchema, response.data || {}, 'POST /api/sku-shelf-index/prefix-map');
    } catch (error: unknown) {
      console.error('Error fetching prefix map:', error);
      return {};
    }
  });
};

/**
 * Fetch order shelf locations: primary location preferred, fallback pick bin then stock.
 * Only returns checked_in: true. Returns { [sku]: { shelfIndex, type } }.
 */
export const fetchOrderShelfLocations = async (
  skus: readonly string[],
): Promise<OrderShelfLocationMap> => {
  const list = [...skus];
  const key = `order-locations:${skuListCacheKey(list)}`;
  return dedupeInFlight(key, async () => {
    try {
      const response = await axios.post('/api/sku-shelf-index/order-locations', { skus: list });
      return validateResponse(OrderShelfLocationMapSchema, response.data || {}, 'POST /api/sku-shelf-index/order-locations');
    } catch (error: unknown) {
      console.error('Error fetching order shelf locations:', error);
      throw error;
    }
  });
};

/**
 * Component source locations for Make Bundles: stock before pick bins, then lowest
 * height (3rd shelf-index field), then lowest aisle (1st).
 */
export const fetchBundleWorkSourceLocations = async (
  skus: readonly string[],
): Promise<OrderShelfLocationMap> => {
  const list = [...skus];
  const key = `bundle-work-source-locations:${skuListCacheKey(list)}`;
  return dedupeInFlight(key, async () => {
    try {
      const response = await axios.post('/api/sku-shelf-index/order-locations', {
        skus: list,
        preference: 'bundle-work-source',
      });
      return validateResponse(
        OrderShelfLocationMapSchema,
        response.data || {},
        'POST /api/sku-shelf-index/order-locations?preference=bundle-work-source',
      );
    } catch (error: unknown) {
      console.error('Error fetching bundle work source locations:', error);
      throw error;
    }
  });
};

/** Parent keeps order pick-bin rules; components use stock-first source ranking. */
export async function fetchBundleWorkShelfLocations(
  parentSku: string,
  skus: readonly string[],
): Promise<OrderShelfLocationMap> {
  const parentNorm = normalizeSku(parentSku);
  const unique = [...new Set(skus.map((sku) => normalizeSku(sku)).filter(Boolean))];
  const sourceSkus = unique.filter((sku) => sku !== parentNorm);
  const [parentMap, sourceMap] = await Promise.all([
    parentNorm ? fetchOrderShelfLocations([parentNorm]) : Promise.resolve({}),
    sourceSkus.length > 0 ? fetchBundleWorkSourceLocations(sourceSkus) : Promise.resolve({}),
  ]);
  return { ...sourceMap, ...parentMap };
}
